import React, { useRef, useState } from 'react';

import { Select } from '@aline/common';
import { ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import { Ban, Download, LogOut, Pencil, UserRoundPen } from 'lucide-react';
import { MoreVerticalIcon } from 'src/components/icons/MoreVerticalIcon';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  menuButton: {
    '& path': {
      fill: theme.palette.common.white,
    },
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
    alignItems: 'center',
    borderRadius: 16,
    cursor: 'pointer',
    display: 'flex',
    height: 32,
    justifyContent: 'center',
    marginRight: theme.spacing(2),
    padding: theme.spacing(0, 1),
    [theme.breakpoints.down('md')]: {
      marginRight: theme.spacing(1),
    },
  },
  menuIcon: {
    '&.MuiListItemIcon-root': {
      color: theme.appColors.iconsType,
      minWidth: 28,
    },
  },
  menuItem: {
    fontSize: 14,
    padding: theme.spacing(1, 2),
  },
}));

export interface WalkthroughOverflowMenuProps {
  readonly canDownload: boolean;
  readonly onDecline: () => void;
  readonly onDownload: () => void;
  readonly onEdit: () => void;
  readonly onEscape: () => void;
  readonly onReassign: () => void;
  readonly recipientId: string | null;
  readonly wasSentForSignature: boolean;
}

// TODO: If changing, consider migrating to DeclarativeMenu
export function WalkthroughOverflowMenu({
  canDownload,
  onDecline,
  onDownload,
  onEdit,
  onEscape,
  onReassign,
  recipientId,
  wasSentForSignature,
}: WalkthroughOverflowMenuProps) {
  const { classes } = useStyles();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuButtonRef = useRef<HTMLDivElement | null>(null);

  function closeAndRun(action: () => void) {
    setMenuOpen(false);
    action();
  }

  return (
    <>
      <div
        ref={menuButtonRef}
        aria-label="More"
        className={classes.menuButton}
        data-cy={Select.document.editor.walkthrough.overflowMenuButton}
        onClick={() => setMenuOpen(!menuOpen)}
      >
        <MoreVerticalIcon />
      </div>
      <Menu
        anchorEl={menuButtonRef.current}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        open={menuOpen}
        // Sit above the editor backdrop, which lives in another stacking context.
        sx={{ zIndex: theme => theme.zIndex.modal + 2 }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        onClose={() => setMenuOpen(false)}
      >
        <WalkthroughOverflowMenuItems
          canDownload={canDownload}
          menuIconClassName={classes.menuIcon}
          menuItemClassName={classes.menuItem}
          recipientId={recipientId}
          wasSentForSignature={wasSentForSignature}
          onDecline={() => closeAndRun(onDecline)}
          onDownload={() => closeAndRun(onDownload)}
          onEdit={() => closeAndRun(onEdit)}
          onEscape={() => closeAndRun(onEscape)}
          onReassign={() => closeAndRun(onReassign)}
        />
      </Menu>
    </>
  );
}

function WalkthroughOverflowMenuItems({
  canDownload,
  menuIconClassName,
  menuItemClassName,
  onDecline,
  onDownload,
  onEdit,
  onEscape,
  onReassign,
  recipientId,
  wasSentForSignature,
}: {
  readonly canDownload: boolean;
  readonly menuIconClassName: string;
  readonly menuItemClassName: string;
  readonly onDecline: () => void;
  readonly onDownload: () => void;
  readonly onEdit: () => void;
  readonly onEscape: () => void;
  readonly onReassign: () => void;
  readonly recipientId: string | null;
  readonly wasSentForSignature: boolean;
}) {
  if (!wasSentForSignature)
    return (
      <MenuItem
        className={menuItemClassName}
        data-cy={Select.document.editor.walkthrough.finishLaterButton}
        onClick={onEdit}
      >
        <ListItemIcon className={menuIconClassName}>
          <Pencil size={18} />
        </ListItemIcon>
        <ListItemText>Edit</ListItemText>
      </MenuItem>
    );

  return (
    <>
      <MenuItem className={menuItemClassName} onClick={onEscape}>
        <ListItemIcon className={menuIconClassName}>
          <LogOut size={18} />
        </ListItemIcon>
        <ListItemText>Escape</ListItemText>
      </MenuItem>
      {Boolean(recipientId) && (
        <MenuItem className={menuItemClassName} onClick={onReassign}>
          <ListItemIcon className={menuIconClassName}>
            <UserRoundPen size={18} />
          </ListItemIcon>
          <ListItemText>Reassign</ListItemText>
        </MenuItem>
      )}
      <MenuItem
        className={menuItemClassName}
        data-cy={Select.document.editor.walkthrough.downloadButton}
        disabled={!canDownload}
        onClick={onDownload}
      >
        <ListItemIcon className={menuIconClassName}>
          <Download size={18} />
        </ListItemIcon>
        <ListItemText>Download</ListItemText>
      </MenuItem>
      <MenuItem className={menuItemClassName} onClick={onDecline}>
        <ListItemIcon className={menuIconClassName}>
          <Ban size={18} />
        </ListItemIcon>
        <ListItemText>Decline to Sign</ListItemText>
      </MenuItem>
    </>
  );
}
